import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { renderMedia } from "../utils/mediaUtils";
import { NextArrow, PrevArrow } from "./sliderArrow";
import styles from "../pages/page2.module.sass";

function useSlidesToShow() {
  const calc = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1920;
    if (w < 766) return 1;
    if (w < 991) return 2;
    if (w < 1350) return 3;
    return 4;
  };
  const [count, setCount] = useState(calc());
  useEffect(() => {
    const onResize = () => setCount(calc());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return count;
}

function CarouselList({
  items = [],
  currentIndex = 0,
  onSelect,
  containerClassName = "",
  itemClassName = "",
  aspectClassName = "aspect-[345/285]",
  followSelection = false,
}) {
  const slidesToShow = useSlidesToShow();
  const [startIndex, setStartIndex] = useState(0);
  const viewportRef = useRef(null);

  const clampedStart = useMemo(() => {
    const maxStart = Math.max(0, items.length - slidesToShow);
    return Math.min(Math.max(0, startIndex), maxStart);
  }, [items.length, slidesToShow, startIndex]);

  useEffect(() => {
    if (!followSelection) return;
    // 僅在啟用時：保證選中項目在可視範圍內（不強制置中）
    if (currentIndex < clampedStart) {
      setStartIndex(currentIndex);
    } else if (currentIndex > clampedStart + slidesToShow - 1) {
      setStartIndex(Math.max(0, currentIndex - slidesToShow + 1));
    }
  }, [currentIndex, slidesToShow, clampedStart, followSelection]);

  const canPrev = clampedStart > 0;
  const canNext = clampedStart + slidesToShow < items.length;

  const next = () => {
    if (!canNext) return;
    setStartIndex((v) => Math.min(v + 1, Math.max(0, items.length - slidesToShow)));
  };

  const prev = () => {
    if (!canPrev) return;
    setStartIndex((v) => Math.max(v - 1, 0));
  };

  return (
    <div className={`relative w-full ${containerClassName}`}>
      {items.length > slidesToShow && (
        <>
          <PrevArrow
            className={`absolute top-1/2 -translate-y-1/2 z-20 ${!canPrev ? "opacity-40 pointer-events-none" : "cursor-pointer"}`}
            customClassName={styles.prevArrow}
            onClick={canPrev ? prev : undefined}
          />
          <NextArrow
            className={`absolute top-1/2 -translate-y-1/2 z-20 ${!canNext ? "opacity-40 pointer-events-none" : "cursor-pointer"}`}
            customClassName={styles.nextArrow}
            onClick={canNext ? next : undefined}
          />
        </>
      )}

      <div
        ref={viewportRef}
        className="overflow-hidden w-full"
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{
            transform: `translateX(-${(clampedStart * 100) / slidesToShow}%)`,
          }}
        >
          {items.map((item, idx) => (
            <div
              key={item.id ?? idx}
              className={`px-2 ${itemClassName}`}
              style={{ flex: `0 0 ${100 / slidesToShow}%` }}
            >
              <div
                role="button"
                data-id={item.id}
                className={`border ${idx === currentIndex ? "ring-2 ring-blue-400" : ""}`}
                onClick={() => onSelect && onSelect(idx)}
              >
                {item.image && (
                  <div className={`${aspectClassName} w-full relative overflow-hidden bg-black`}>
                    {renderMedia(
                      item.image,
                      item.title,
                      "absolute inset-0 w-full h-full object-contain"
                    )}
                  </div>
                )}
                <div className="p-4 h-[80px] flex items-center">
                  <h3 className="text-xl font-medium text-gray-800 line-clamp-2 leading-6">
                    {item.title ? item.title : ""}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

CarouselList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      image: PropTypes.string,
    })
  ),
  currentIndex: PropTypes.number,
  onSelect: PropTypes.func,
  containerClassName: PropTypes.string,
  itemClassName: PropTypes.string,
  aspectClassName: PropTypes.string,
  followSelection: PropTypes.bool,
};

export default CarouselList;


